import { useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';

export function useAuth() {
  const { 
    user, 
    tokens, 
    isAuthenticated, 
    isLoading, 
    error,
    login, 
    register, 
    logout, 
    refreshToken,
    setUser,
    clearError,
    initializeAuth 
  } = useAuthStore();

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    initializeAuth();
  }, [initializeAuth]);

  const handleLogin = useCallback(async (email: string, password: string) => {
    await login(email, password);
    const from = location.state?.from?.pathname || '/dashboard';
    navigate(from, { replace: true });
  }, [login, navigate, location.state]);

  const handleRegister = useCallback(async (email: string, password: string, confirmPassword: string) => {
    await register(email, password, confirmPassword);
    navigate('/auth/verify-email', { replace: true });
  }, [register, navigate]);

  const handleLogout = useCallback(async () => {
    await logout();
    navigate('/auth/login', { replace: true });
  }, [logout, navigate]);

  const hasRole = useCallback((role: string) => {
    return user?.roles?.includes(role) ?? false;
  }, [user]);

  const hasAnyRole = useCallback((roles: string[]) => {
    return roles.some((role) => user?.roles?.includes(role) ?? false);
  }, [user]);

  return {
    user,
    tokens,
    isAuthenticated,
    isLoading,
    error,
    login: handleLogin,
    register: handleRegister,
    logout: handleLogout,
    refreshToken,
    setUser,
    clearError,
    hasRole,
    hasAnyRole,
  };
}

export function useRequiredAuth(redirectTo = '/auth/login') {
  const { isAuthenticated, isLoading, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      navigate(redirectTo, { 
        replace: true, 
        state: { from: location } 
      });
    }
  }, [isAuthenticated, isLoading, navigate, redirectTo, location]);

  return { isAuthenticated, isLoading, user };
}

export function useGuestOnly(redirectTo = '/dashboard') {
  const { isAuthenticated, isLoading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isLoading && isAuthenticated) {
      navigate(redirectTo, { replace: true });
    }
  }, [isAuthenticated, isLoading, navigate, redirectTo]);

  return { isAuthenticated, isLoading };
}